/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import toast from "react-hot-toast";
import { CompanyService } from "services/companyService";
import { AccountingStatement4Service } from "services/accounting-statement-4";
import { formatDate, formatVndIntegerValue } from "utils";
import DataTable from "./DataTable";
import ActionCell from "./components/ActionCell";

const currentYear = String(new Date().getFullYear());
const currentMonth = String(new Date().getMonth() + 1);

const tableData = {
  columns: [
    { Header: "Số CT", accessor: "voucherNumber", align: "center" },
    { Header: "Ngày CT", accessor: "voucherDate", align: "center" },
    { Header: "Diễn giải", accessor: "description" },
    // 10 columns for debit accounts will be added dynamically
    { Header: "Tổng cộng", accessor: "totalCreditCost", align: "right" },
    { Header: "Thao tác", accessor: "action", sorted: false, align: "center" },
  ],
  rows: [],
};

export default function AccountingTable4() {
  const [companyId, setCompanyId] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [dataTableState, setDataTableState] = useState(tableData);
  const [summary, setSummary] = useState({
    totalRows: 0,
    totalCreditCost: 0,
  });
  const [filters, setFilters] = useState({
    year: currentYear,
    month: currentMonth,
    fromDate: "",
    toDate: "",
    accountCode: "",
    searchBy: "",
  });
  const [query, setQuery] = useState({
    year: currentYear,
    month: currentMonth,
    fromDate: "",
    toDate: "",
    accountCode: "",
    searchBy: "",
  });
  const [paginationData, setPaginationData] = useState({ page: 1, count: 0, size: 10 });

  useEffect(() => {
    const init = async () => {
      try {
        const response = await CompanyService.getByAuth();
        const payload = response?.data || response || {};
        setCompanyId(payload?._id || "");
        setCompanyData(payload);
      } catch (error) {
        toast.error("Không thể lấy thông tin công ty");
      }
    };
    init();
  }, []);

  const fetchAccounts = useCallback(async () => {
    if (!companyId || !filters.year) return;
    try {
      setAccountsLoading(true);
      const response = await AccountingStatement4Service.findAccounts({
        company: companyId,
        year: filters.year,
      });
      const payload = response?.data || response || {};
      const items = payload.items || [];
      setAccounts(items);

      if (items.length > 0 && !filters.accountCode) {
        const firstAccount = items[0].accountCode;
        setFilters((prev) => ({ ...prev, accountCode: firstAccount }));
        setQuery((prev) => ({ ...prev, accountCode: firstAccount }));
      }
    } catch (error) {
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [companyId, filters.year]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const fetchData = useCallback(async () => {
    if (!companyId || !query.year) {
      setDataTableState((prev) => ({ ...prev, rows: [] }));
      setSummary({ totalRows: 0, totalCreditCost: 0 });
      return;
    }

    try {
      setLoading(true);
      const response = await AccountingStatement4Service.preview({
        company: companyId,
        year: query.year,
        month: query.month || undefined,
        fromDate: query.fromDate || undefined,
        toDate: query.toDate || undefined,
        accountCode: query.accountCode,
      });
      const payload = response?.data || response || {};
      
      const debitAccountLabels = payload.debitAccountLabels || Array(10).fill("");
      
      // Reconstruct columns based on debitAccountLabels
      const dynamicColumns = [
        { Header: "Số CT", accessor: "voucherNumber", align: "center" },
        { Header: "Ngày CT", accessor: "voucherDate", align: "center" },
        { Header: "Diễn giải", accessor: "description" },
      ];

      debitAccountLabels.forEach((label, index) => {
        if (label) {
          dynamicColumns.push({
            Header: `Nợ TK ${label}`,
            accessor: `debitValue_${index}`,
            align: "right",
          });
        }
      });

      dynamicColumns.push({ Header: "Tổng cộng", accessor: "totalCreditCost", align: "right" });
      dynamicColumns.push({ Header: "Thao tác", accessor: "action", sorted: false, align: "center" });

      const rows = (payload.rows || []).map((item) => {
        const params = new URLSearchParams({
          year: query.year,
          month: query.month || "",
          fromDate: query.fromDate || "",
          toDate: query.toDate || "",
          accountCode: query.accountCode || "",
        }).toString();

        const rowData = {
          voucherNumber: item.voucherNumber || "",
          voucherDate: formatDate(item.voucherDate),
          description: item.description || "",
          totalCreditCost: formatVndIntegerValue(item.totalCreditCost || 0),
          action: <ActionCell item={{ viewLink: `/templates/accounting-table-4/view?${params}` }} />,
        };

        (item.debitValues || []).forEach((val, idx) => {
          if (debitAccountLabels[idx]) {
            rowData[`debitValue_${idx}`] = formatVndIntegerValue(val || 0);
          }
        });

        return rowData;
      });

      setDataTableState({ columns: dynamicColumns, rows });
      setSummary({
        totalRows: rows.length,
        totalCreditCost: payload.totals?.totalCreditCost || 0,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải Bảng kê số 4");
    } finally {
      setLoading(false);
    }
  }, [companyId, query]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    if (!companyId || !filters.year || !filters.month) {
      toast.error("Thiếu thông tin công ty, năm hoặc tháng");
      return;
    }
    try {
      setSyncing(true);
      // First get preview data to sync
      const previewResponse = await AccountingStatement4Service.preview({
        company: companyId,
        year: filters.year,
        month: filters.month,
      });
      const previewData = previewResponse?.data || previewResponse || {};
      
      await AccountingStatement4Service.sync({
        company: companyId,
        year: filters.year,
        month: filters.month,
        unit: companyData?.name || "Tên công ty",
        address: companyData?.address || "Địa chỉ",
        debitAccountLabels: previewData.debitAccountLabels,
        rows: previewData.rows,
        totals: previewData.totals,
      });
      
      toast.success("Đồng bộ dữ liệu Bảng kê số 4 thành công");
      fetchAccounts();
      setQuery((prev) => ({ ...prev, ...filters }));
      setPaginationData((prev) => ({ ...prev, page: 1 }));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Đồng bộ dữ liệu thất bại");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card style={{ height: "100%" }}>
          <DataTable
            table={dataTableState}
            loading={loading}
            syncing={syncing}
            filters={filters}
            setFilters={setFilters}
            accounts={accounts}
            accountsLoading={accountsLoading}
            query={query}
            setQuery={setQuery}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            onSync={handleSync}
            onFetchData={(newFilters) => {
              setQuery((prev) => ({ ...prev, ...newFilters }));
              setPaginationData((prev) => ({ ...prev, page: 1 }));
            }}
          />
          <SoftBox px={3} pb={3} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <SoftTypography variant="caption" color="text">
              Tổng số dòng: {summary.totalRows}
            </SoftTypography>
            <SoftTypography variant="caption" color="text" textAlign="right">
              Tổng cộng chi phí: {formatVndIntegerValue(summary.totalCreditCost || 0)}
            </SoftTypography>
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}
